import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllTables } from '../data/restaurantTables';
import { Users, Check, ArrowRight, ArrowLeft, Grid, Home } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
// import TopNavigation from './shared/TopNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const InteractiveTableBooking = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedTable, setSelectedTable] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'photo'
  const [bookingData, setBookingData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    guests: 2,
    specialRequests: ''
  });
  const [bookedTables, setBookedTables] = useState([]);
  const [tablesWithConflicts, setTablesWithConflicts] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const allTables = getAllTables();

  const groupedTables = {
    window: allTables.filter(t => t.type === 'window' && !t.id.includes('WIN')),
    cabinet: allTables.filter(t => t.type === 'cabinet'),
    booth: allTables.filter(t => t.type === 'booth'),
    group: allTables.filter(t => t.type === 'group'),
    special: allTables.filter(t => t.type === 'special')
  };

  useEffect(() => {
    if (step === 2 && bookingData.date && bookingData.time) {
      fetchBookedTables();
    }
  }, [step]);

  const fetchBookedTables = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/table-reservations`, {
        params: { date: bookingData.date, time: bookingData.time }
      });
      setBookedTables(response.data.bookedTableIds || []);
      setTablesWithConflicts(response.data.tablesWithConflicts || {});
    } catch (error) {
      console.error('Error fetching booked tables:', error);
    }
  };

  const isTableAvailable = (tableId) => !bookedTables.includes(tableId);
  const hasConflict = (tableId) => tablesWithConflicts[tableId]?.length > 0;
  const getConflictInfo = (tableId) => tablesWithConflicts[tableId] || [];

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!bookingData.name || !bookingData.phone || !bookingData.email || !bookingData.date || !bookingData.time) {
      alert('Please fill all required fields');
      return;
    }
    setStep(2);
  };

  const handleTableSelect = (table) => {
    // Toggle selection - if already selected, unselect it
    if (selectedTable?.id === table.id) {
      setSelectedTable(null);
      return;
    }
    
    if (!isTableAvailable(table.id)) {
      alert('This table is already booked for this time');
      return;
    }
    if (table.capacity < bookingData.guests) {
      alert(`This table seats only ${table.capacity} people. You need ${bookingData.guests} seats.`);
      return;
    }
    setSelectedTable(table);
  };

  const handleFinalSubmit = async () => {
    if (!selectedTable) {
      alert('Please select a table');
      return;
    }
    setIsSubmitting(true);
    try {
      const reservationData = {
        ...bookingData,
        tableId: selectedTable.id,
        tableName: selectedTable.name,
        tableCapacity: selectedTable.capacity,
        tableType: selectedTable.type,
        status: 'confirmed',
        createdAt: new Date().toISOString()
      };
      const response = await axios.post(`${BACKEND_URL}/api/table-reservations`, reservationData);
      if (response.data.success) {
        setBookingSuccess(true);
        setTimeout(() => navigate('/'), 3000);
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      
      // Show specific error message for double booking
      if (error.response && error.response.status === 400) {
        alert(error.response.data.detail || 'This table is already booked. Please select another table or time.');
        // Refresh the booked tables list
        fetchBookedTables();
        // Go back to table selection
        setSelectedTable(null);
      } else {
        alert('Error creating reservation. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (bookingSuccess) {
    const isAutoConfirmed = bookingData.guests < 10;
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#1a1a1a' }}>
        <div className="rounded-3xl p-12 text-center max-w-2xl" style={{ background: 'rgba(34, 197, 94, 0.2)', border: '2px solid rgb(34, 197, 94)' }}>
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce" style={{ background: 'rgb(34, 197, 94)' }}>
            <Check className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold mb-4" style={{ color: 'rgb(134, 239, 172)' }}>
            {isAutoConfirmed ? '⚡ Automatically Confirmed!' : '✅ Reservation Confirmed!'}
          </h2>
          {isAutoConfirmed && (
            <div className="mb-6 p-4 rounded-xl" style={{ background: 'rgba(59, 130, 246, 0.2)', border: '2px solid rgb(59, 130, 246)' }}>
              <p className="font-bold mb-2" style={{ color: 'rgb(147, 197, 253)' }}>
                ⚡ INSTANT CONFIRMATION - No Need to Wait!
              </p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                For parties under 10 people, your reservation is automatically confirmed.
              </p>
            </div>
          )}
          {!isAutoConfirmed && (
            <div className="mb-6 p-4 rounded-xl" style={{ background: 'rgba(251, 191, 36, 0.2)', border: '2px solid rgb(251, 191, 36)' }}>
              <p className="font-bold mb-2" style={{ color: 'rgb(253, 224, 71)' }}>
                📞 We'll Call You Soon
              </p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                For large parties (10+ guests), we'll call you within 2 hours to confirm.
              </p>
            </div>
          )}
          <p className="text-white text-lg mb-2">Thank you, {bookingData.name}!</p>
          <p className="text-white/80">Your table has been reserved at {selectedTable?.name}</p>
          <p className="text-white/80">{bookingData.date} at {bookingData.time}</p>
          <p className="text-white/60 mt-4 text-sm">Check your email for confirmation details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#1a1a1a' }}>
      {/* Top Navigation - Use Shared Component */}
      {/* <TopNavigation /> - moved to App.js */}
      
      
      <div className="max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">🪑 Reserve Your Table</h1>
          <p className="text-white/70 text-lg">{step === 1 ? 'Step 1: Enter your details' : 'Step 2: Choose your table'}</p>
          <div className="flex justify-center gap-4 mt-6">
            <div className={`flex items-center gap-2 ${step === 1 ? 'text-amber-400' : 'text-green-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 1 ? 'bg-amber-500' : 'bg-green-500'}`}>
                {step === 1 ? '1' : <Check className="w-5 h-5" />}
              </div>
              <span className="font-semibold">Your Details</span>
            </div>
            <div className="w-12 border-t-2 border-white/20 self-center"></div>
            <div className={`flex items-center gap-2 ${step === 2 ? 'text-amber-400' : 'text-white/40'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 2 ? 'bg-amber-500' : 'bg-white/20'}`}>2</div>
              <span className="font-semibold">Select Table</span>
            </div>
          </div>
        </div>

        {step === 1 && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-lg rounded-3xl p-8 border-2 border-amber-400/30">
              <h2 className="text-2xl font-bold text-amber-300 mb-6">Reservation Details</h2>
              <form onSubmit={handleStep1Submit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">Date *</label>
                    <input type="date" value={bookingData.date} min={new Date().toISOString().split('T')[0]} onChange={(e) => setBookingData({...bookingData, date: e.target.value})} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white" required />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">Time *</label>
                    <select value={bookingData.time} onChange={(e) => setBookingData({...bookingData, time: e.target.value})} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white" required>
                      <option value="">Select time</option>
                      <option value="11:00">11:00</option>
                      <option value="11:30">11:30</option>
                      <option value="12:00">12:00</option>
                      <option value="12:30">12:30</option>
                      <option value="13:00">13:00</option>
                      <option value="13:30">13:30</option>
                      <option value="14:00">14:00</option>
                      <option value="14:30">14:30</option>
                      <option value="17:00">17:00</option>
                      <option value="17:30">17:30</option>
                      <option value="18:00">18:00</option>
                      <option value="18:30">18:30</option>
                      <option value="19:00">19:00</option>
                      <option value="19:30">19:30</option>
                      <option value="20:00">20:00</option>
                      <option value="20:30">20:30</option>
                      <option value="21:00">21:00</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Number of Guests *</label>
                  <input type="number" min="1" max="12" value={bookingData.guests} onChange={(e) => setBookingData({...bookingData, guests: parseInt(e.target.value)})} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white" required />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Name *</label>
                  <input type="text" value={bookingData.name} onChange={(e) => setBookingData({...bookingData, name: e.target.value})} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white" required />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Phone *</label>
                  <input type="tel" value={bookingData.phone} onChange={(e) => setBookingData({...bookingData, phone: e.target.value})} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white" required />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Email *</label>
                  <input type="email" value={bookingData.email} onChange={(e) => setBookingData({...bookingData, email: e.target.value})} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white" required />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Special Requests (Optional)</label>
                  <textarea value={bookingData.specialRequests} onChange={(e) => setBookingData({...bookingData, specialRequests: e.target.value})} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white" rows="3" placeholder="Birthday celebration, dietary requirements, etc." />
                </div>
                <button type="submit" className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-lg rounded-xl shadow-xl transition-all hover:scale-105 flex items-center justify-center gap-2">
                  Continue to Table Selection <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-white/70 hover:text-white transition-all">
              <ArrowLeft className="w-5 h-5" /> Back to Edit Details
            </button>
            
            <div className="bg-blue-500/10 border-2 border-blue-400/30 rounded-xl p-6">
              <h3 className="text-xl font-bold text-blue-300 mb-3">Your Reservation:</h3>
              <div className="grid md:grid-cols-4 gap-4 text-white">
                <div><p className="text-white/60 text-sm">Date</p><p className="font-bold">{bookingData.date}</p></div>
                <div><p className="text-white/60 text-sm">Time</p><p className="font-bold">{bookingData.time}</p></div>
                <div><p className="text-white/60 text-sm">Guests</p><p className="font-bold">{bookingData.guests} people</p></div>
                <div><p className="text-white/60 text-sm">Name</p><p className="font-bold">{bookingData.name}</p></div>
              </div>
            </div>

            <div className="flex justify-center gap-4 mb-6">
              <button onClick={() => setViewMode('grid')} className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${viewMode === 'grid' ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
                <Grid className="w-5 h-5" /> List View
              </button>
              <button onClick={() => setViewMode('photo')} className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${viewMode === 'photo' ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
                📸 Photo View
              </button>
            </div>

            {viewMode === 'photo' && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold text-amber-300 text-center mb-6">Click on Tables in Real Photos</h2>
                <p className="text-white/70 text-center mb-8">Click directly on any table or chair area in the photos below to select it</p>
                
                {/* Window Tables Photo */}
                <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl p-6 border-2 border-blue-400/30">
                  <h3 className="text-2xl font-bold text-blue-300 mb-4 flex items-center gap-2">
                    🪟 Window Tables (4+4 seats)
                  </h3>
                  <div className="relative group">
                    <img src="https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/obvc9vi5_Windows%20table%20next%20to%20door%204%2B4.jpg" alt="Window Tables" className="w-full rounded-xl" />
                    {/* Clickable overlay areas for Window Table 1 (left side) */}
                    <div 
                      onClick={() => handleTableSelect(groupedTables.window[0])}
                      className={`absolute top-[20%] left-[5%] w-[40%] h-[70%] cursor-pointer transition-all ${
                        selectedTable?.id === groupedTables.window[0]?.id 
                          ? 'bg-amber-500/60 border-4 border-amber-400' 
                          : !isTableAvailable(groupedTables.window[0]?.id)
                          ? 'bg-red-500/40 border-4 border-red-400 cursor-not-allowed'
                          : 'bg-blue-500/0 hover:bg-blue-500/30 border-2 border-blue-400/0 hover:border-blue-400'
                      } rounded-xl`}
                      title={`${groupedTables.window[0]?.name} - ${groupedTables.window[0]?.capacity} seats ${!isTableAvailable(groupedTables.window[0]?.id) ? '- BOOKED' : ''}`}
                    >
                      {selectedTable?.id === groupedTables.window[0]?.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-amber-500 text-white px-6 py-3 rounded-lg font-bold text-xl shadow-2xl">
                            ✓ SELECTED
                          </div>
                        </div>
                      )}
                      {!isTableAvailable(groupedTables.window[0]?.id) && selectedTable?.id !== groupedTables.window[0]?.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-red-500 text-white px-6 py-3 rounded-lg font-bold text-xl shadow-2xl">
                            🔒 BOOKED
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Clickable overlay areas for Window Table 2 (right side) */}
                    <div 
                      onClick={() => handleTableSelect(groupedTables.window[1])}
                      className={`absolute top-[20%] right-[5%] w-[40%] h-[70%] cursor-pointer transition-all ${
                        selectedTable?.id === groupedTables.window[1]?.id 
                          ? 'bg-amber-500/60 border-4 border-amber-400' 
                          : !isTableAvailable(groupedTables.window[1]?.id)
                          ? 'bg-red-500/40 border-4 border-red-400 cursor-not-allowed'
                          : 'bg-blue-500/0 hover:bg-blue-500/30 border-2 border-blue-400/0 hover:border-blue-400'
                      } rounded-xl`}
                      title={`${groupedTables.window[1]?.name} - ${groupedTables.window[1]?.capacity} seats ${!isTableAvailable(groupedTables.window[1]?.id) ? '- BOOKED' : ''}`}
                    >
                      {selectedTable?.id === groupedTables.window[1]?.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-amber-500 text-white px-6 py-3 rounded-lg font-bold text-xl shadow-2xl">
                            ✓ SELECTED
                          </div>
                        </div>
                      )}
                      {!isTableAvailable(groupedTables.window[1]?.id) && selectedTable?.id !== groupedTables.window[1]?.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-red-500 text-white px-6 py-3 rounded-lg font-bold text-xl shadow-2xl">
                            🔒 BOOKED
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Cabinet Area Photo */}
                <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl p-6 border-2 border-cyan-400/30">
                  <h3 className="text-2xl font-bold text-cyan-300 mb-4 flex items-center gap-2">
                    🏔️ Cabinet Area (Multiple Tables)
                  </h3>
                  <div className="relative group">
                    <img src="https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/l4ctlh45_cabinet%201%20full%20photo.jpg" alt="Cabinet Area" className="w-full rounded-xl" />
                    {/* Left side tables - multiple small clickable areas */}
                    {groupedTables.cabinet.slice(0, 3).map((table, idx) => (
                      <div 
                        key={table.id}
                        onClick={() => handleTableSelect(table)}
                        className={`absolute left-[8%] w-[25%] h-[22%] cursor-pointer transition-all ${selectedTable?.id === table.id ? 'bg-amber-500/60 border-4 border-amber-400' : 'bg-cyan-500/0 hover:bg-cyan-500/30 border-2 border-cyan-400/0 hover:border-cyan-400'} rounded-xl`}
                        style={{ top: `${25 + (idx * 25)}%` }}
                        title={`${table.name} - ${table.capacity} seats`}
                      >
                        {selectedTable?.id === table.id && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-amber-500 text-white px-4 py-2 rounded-lg font-bold shadow-2xl">
                              ✓ {table.capacity} SEATS
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {/* Right side tables */}
                    {groupedTables.cabinet.slice(3, 6).map((table, idx) => (
                      <div 
                        key={table.id}
                        onClick={() => handleTableSelect(table)}
                        className={`absolute right-[8%] w-[25%] h-[22%] cursor-pointer transition-all ${selectedTable?.id === table.id ? 'bg-amber-500/60 border-4 border-amber-400' : 'bg-cyan-500/0 hover:bg-cyan-500/30 border-2 border-cyan-400/0 hover:border-cyan-400'} rounded-xl`}
                        style={{ top: `${25 + (idx * 25)}%` }}
                        title={`${table.name} - ${table.capacity} seats`}
                      >
                        {selectedTable?.id === table.id && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-amber-500 text-white px-4 py-2 rounded-lg font-bold shadow-2xl">
                              ✓ {table.capacity} SEATS
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Corner Sofa Booth Photo */}
                <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl p-6 border-2 border-purple-400/30">
                  <h3 className="text-2xl font-bold text-purple-300 mb-4 flex items-center gap-2">
                    🛋️ Corner Sofa Booth (6+4 seats)
                  </h3>
                  <div className="relative group">
                    <img src="https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/fp25o0oh_corner%20sofa%20table%206%2B4.jpg" alt="Corner Sofa Booth" className="w-full rounded-xl" />
                    {/* Left booth section (6 seats) */}
                    <div 
                      onClick={() => handleTableSelect(groupedTables.booth[0])}
                      className={`absolute top-[25%] left-[5%] w-[45%] h-[60%] cursor-pointer transition-all ${selectedTable?.id === groupedTables.booth[0]?.id ? 'bg-amber-500/60 border-4 border-amber-400' : 'bg-purple-500/0 hover:bg-purple-500/30 border-2 border-purple-400/0 hover:border-purple-400'} rounded-xl`}
                      title={`${groupedTables.booth[0]?.name} - ${groupedTables.booth[0]?.capacity} seats`}
                    >
                      {selectedTable?.id === groupedTables.booth[0]?.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-amber-500 text-white px-6 py-3 rounded-lg font-bold text-xl shadow-2xl">
                            ✓ 6 SEATS
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Right section (4 seats) */}
                    <div 
                      onClick={() => handleTableSelect(groupedTables.booth[1])}
                      className={`absolute top-[25%] right-[5%] w-[40%] h-[60%] cursor-pointer transition-all ${selectedTable?.id === groupedTables.booth[1]?.id ? 'bg-amber-500/60 border-4 border-amber-400' : 'bg-purple-500/0 hover:bg-purple-500/30 border-2 border-purple-400/0 hover:border-purple-400'} rounded-xl`}
                      title={`${groupedTables.booth[1]?.name} - ${groupedTables.booth[1]?.capacity} seats`}
                    >
                      {selectedTable?.id === groupedTables.booth[1]?.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-amber-500 text-white px-6 py-3 rounded-lg font-bold text-xl shadow-2xl">
                            ✓ 4 SEATS
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Center Group Table Photo */}
                <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl p-6 border-2 border-green-400/30">
                  <h3 className="text-2xl font-bold text-green-300 mb-4 flex items-center gap-2">
                    👥 Center Group Table (12 seats)
                  </h3>
                  <div className="relative group">
                    <img src="https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/lkwpq49f_group%20table%20of%2012.jpg" alt="Center Group Table" className="w-full rounded-xl" />
                    {/* Entire table is clickable */}
                    <div 
                      onClick={() => handleTableSelect(groupedTables.group[0])}
                      className={`absolute top-[15%] left-[15%] w-[70%] h-[70%] cursor-pointer transition-all ${selectedTable?.id === groupedTables.group[0]?.id ? 'bg-amber-500/60 border-4 border-amber-400' : 'bg-green-500/0 hover:bg-green-500/30 border-2 border-green-400/0 hover:border-green-400'} rounded-xl`}
                      title={`${groupedTables.group[0]?.name} - ${groupedTables.group[0]?.capacity} seats`}
                    >
                      {selectedTable?.id === groupedTables.group[0]?.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-amber-500 text-white px-8 py-4 rounded-lg font-bold text-2xl shadow-2xl">
                            ✓ 12 SEATS SELECTED
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Decorative Hut Photo */}
                <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl p-6 border-2 border-pink-400/30">
                  <h3 className="text-2xl font-bold text-pink-300 mb-4 flex items-center gap-2">
                    ✨ Decorative Hut VIP (4+4 seats)
                  </h3>
                  <div className="relative group">
                    <img src="https://customer-assets.emergentagent.com/job_8f790146-acd4-4a9a-93d7-80916b412d08/artifacts/l3hnz07u_decorative%20hut%20tables%202%2B2.jpg" alt="Decorative Hut" className="w-full rounded-xl" />
                    {/* Left hut table (4 seats) */}
                    <div 
                      onClick={() => handleTableSelect(groupedTables.special[0])}
                      className={`absolute top-[30%] left-[10%] w-[35%] h-[50%] cursor-pointer transition-all ${selectedTable?.id === groupedTables.special[0]?.id ? 'bg-amber-500/60 border-4 border-amber-400' : 'bg-pink-500/0 hover:bg-pink-500/30 border-2 border-pink-400/0 hover:border-pink-400'} rounded-xl`}
                      title={`${groupedTables.special[0]?.name} - ${groupedTables.special[0]?.capacity} seats`}
                    >
                      {selectedTable?.id === groupedTables.special[0]?.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-amber-500 text-white px-6 py-3 rounded-lg font-bold text-xl shadow-2xl">
                            ✓ VIP 4
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Right hut table (4 seats) */}
                    <div 
                      onClick={() => handleTableSelect(groupedTables.special[1])}
                      className={`absolute top-[30%] right-[10%] w-[35%] h-[50%] cursor-pointer transition-all ${selectedTable?.id === groupedTables.special[1]?.id ? 'bg-amber-500/60 border-4 border-amber-400' : 'bg-pink-500/0 hover:bg-pink-500/30 border-2 border-pink-400/0 hover:border-pink-400'} rounded-xl`}
                      title={`${groupedTables.special[1]?.name} - ${groupedTables.special[1]?.capacity} seats`}
                    >
                      {selectedTable?.id === groupedTables.special[1]?.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-amber-500 text-white px-6 py-3 rounded-lg font-bold text-xl shadow-2xl">
                            ✓ VIP 4
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-center mt-8">
                  <p className="text-white/60 text-sm">💡 Hover over any dining area to see it highlight, then click to select</p>
                  <p className="text-white/60 text-sm mt-2">Click on a selected table again to deselect it</p>
                </div>
              </div>
            )}

            {viewMode === 'grid' && (
              <>
                <h2 className="text-3xl font-bold text-amber-300 text-center">Choose Your Table Area</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.keys(groupedTables).map((type) => {
                    if (groupedTables[type].length === 0) return null;
                    const areaNames = { 
                      window: '🪟 Window Tables', 
                      cabinet: '🏔️ Cabinet Area', 
                      booth: '🛋️ Corner Sofa Booth', 
                      group: '👥 Center Group Table', 
                      special: '✨ Decorative Hut (VIP)' 
                    };
                    const areaDesc = { 
                      window: 'Street view, natural light, by door', 
                      cabinet: 'Mountain art wall, quiet seating, multiple table sizes', 
                      booth: 'Comfortable leather booth, next to bar', 
                      group: 'Large 12-seat table, perfect for celebrations', 
                      special: 'Traditional carved wood hut, private VIP setting' 
                    };
                    
                    const photo = groupedTables[type][0]?.photo;
                    
                    return (
                      <div key={type} className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-2xl overflow-hidden border-2 border-white/20 hover:border-amber-400 transition-all">
                        {photo && <img src={photo} alt={areaNames[type]} className="w-full h-48 object-cover" />}
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-white mb-2">{areaNames[type]}</h3>
                          <p className="text-white/70 text-sm mb-4">{areaDesc[type]}</p>
                          <div className="space-y-2">
                            {groupedTables[type].map(table => {
                              const isBooked = !isTableAvailable(table.id);
                              const tooSmall = table.capacity < bookingData.guests;
                              const isAvailable = isTableAvailable(table.id) && table.capacity >= bookingData.guests;
                              const isSelected = selectedTable?.id === table.id;
                              const tableHasConflict = hasConflict(table.id);
                              const conflictInfo = getConflictInfo(table.id);
                              
                              return (
                                <button 
                                  key={table.id} 
                                  onClick={() => handleTableSelect(table)} 
                                  disabled={isBooked || tooSmall} 
                                  className={`w-full p-4 rounded-lg border-2 transition-all transform hover:scale-102 ${
                                    isSelected 
                                      ? 'bg-amber-500/40 border-amber-400 ring-4 ring-amber-400/50 scale-105' 
                                      : isAvailable && tableHasConflict
                                      ? 'bg-yellow-500/10 border-yellow-400/50 hover:border-yellow-400 hover:bg-yellow-500/20'
                                      : isAvailable 
                                      ? 'bg-white/5 border-green-400/50 hover:border-green-400 hover:bg-green-500/10' 
                                      : isBooked
                                      ? 'bg-red-500/20 border-red-400/50 opacity-70 cursor-not-allowed'
                                      : 'bg-gray-500/20 border-gray-400/50 opacity-60 cursor-not-allowed'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-white font-semibold text-lg">{table.name}</span>
                                    <div className="flex items-center gap-2">
                                      {isBooked && <span className="text-red-400 font-bold text-sm">🔒 BOOKED</span>}
                                      {isAvailable && !isSelected && tableHasConflict && <span className="text-yellow-400 font-bold text-sm">⚡ AVAILABLE</span>}
                                      {isAvailable && !isSelected && !tableHasConflict && <span className="text-green-400 font-bold text-sm">✓ AVAILABLE</span>}
                                      {isSelected && <span className="text-amber-400 font-bold text-sm">✓ SELECTED</span>}
                                      <div className="flex items-center gap-2 text-white/70 text-sm">
                                        <Users className="w-5 h-5" />
                                        <span className="font-bold text-lg">{table.capacity}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {Array.from({length: table.capacity}).map((_, i) => (
                                      <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                        isBooked ? 'bg-red-500/30' : isSelected ? 'bg-amber-500/50' : tableHasConflict ? 'bg-yellow-500/30' : 'bg-white/20'
                                      }`}>
                                        {isSelected ? '✓' : isBooked ? '✗' : '👤'}
                                      </div>
                                    ))}
                                  </div>
                                  {isSelected && <p className="text-amber-300 text-sm mt-2 font-bold">✓ SELECTED - Click again to deselect</p>}
                                  {isBooked && <p className="text-red-300 text-sm mt-2 font-bold">✗ Already Booked for this time</p>}
                                  {tooSmall && !isBooked && <p className="text-orange-300 text-sm mt-2 font-bold">⚠ Too small for {bookingData.guests} guests</p>}
                                  {isAvailable && !isSelected && tableHasConflict && (
                                    <div className="mt-2">
                                      <p className="text-yellow-300 text-sm font-bold">⚡ Available - Nearby booking exists</p>
                                      <p className="text-yellow-200/70 text-xs mt-1">📋 Other bookings: {conflictInfo.map(c => c.time).join(', ')}</p>
                                      <p className="text-yellow-200/70 text-xs">We'll arrange suitable seating for you!</p>
                                    </div>
                                  )}
                                  {isAvailable && !isSelected && !tableHasConflict && <p className="text-green-300 text-sm mt-2 font-bold">✓ Available - Click to reserve</p>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Map view removed */}
            
            {selectedTable && (
              <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/95 to-transparent p-6 z-50">
                <div className="max-w-4xl mx-auto">
                  <div className="bg-green-500/20 border-2 border-green-400 rounded-xl p-4 mb-4">
                    <p className="text-green-300 font-bold text-center">✓ Selected: {selectedTable.name} (Seats {selectedTable.capacity})</p>
                  </div>
                  <button onClick={handleFinalSubmit} disabled={isSubmitting} className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-lg rounded-xl shadow-xl transition-all hover:scale-105 flex items-center justify-center gap-2">
                    {isSubmitting ? 'Confirming...' : 'Confirm Reservation'} <Check className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveTableBooking;
